import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { EmployeePageConfig, EmployeePageConfigType } from './employee';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

export type KnowledgeSource = Omit<
  components['schemas']['KnowledgeSourceDetail'],
  'processing_detail'
> & {
  processing_details: components['schemas']['KnowledgeSourceDetail']['processing_detail'];
};
export type OnlineSource = Omit<
  components['schemas']['KnowledgeBaseCrawlConfig'],
  'include_patterns' | 'exclude_patterns'
> & {
  include_patterns: string[];
  exclude_patterns: string[];
};
type OnlineSourceListItemRaw =
  components['schemas']['KnowledgeBaseCrawlConfigList'];

export type OnlineSourceExecution =
  components['schemas']['KnowledgeBaseCrawlExecutionList'];

export type OnlineSourceListItem = Omit<
  OnlineSourceListItemRaw,
  'latest_execution' | 'active_execution'
> & {
  latest_execution: OnlineSourceExecution | null;
  active_execution: OnlineSourceExecution | null;
};

export type OnlineSourcePageListItemRaw =
  paths['/api/knowledge/crawl/pages/search']['get']['responses']['200']['content']['application/json']['results'][0];

export type OnlineSourcePageListItem = Omit<
  OnlineSourcePageListItemRaw,
  'crawl_config' | 'knowledge_source'
> & {
  crawl_config: OnlineSourceListItem;
  knowledge_source: KnowledgeSource & {
    is_active: boolean;
    is_active_by_user: boolean;
  };
};

type SchemaKnowledgeSourceSearchParams =
  paths['/api/knowledge/source/search']['get']['parameters']['query'];
export interface KnowledgeSourceSearchParams
  extends SchemaKnowledgeSourceSearchParams {
  catalog_id: string;
}
export type KnowledgeSourceSearchResponse =
  paths['/api/knowledge/source/search']['get']['responses']['200']['content']['application/json'];

export type KnowledgeSourceCreateRequestBody =
  paths['/api/knowledge/source/add']['post']['requestBody']['content']['application/json'];
export type KnowledgeSourceCreateResponse =
  paths['/api/knowledge/source/add']['post']['responses']['201']['content']['application/json'];

export type KnowledgeSourceDeleteResponse =
  paths['/api/knowledge/source/{id}']['delete']['responses']['204']['content'];

export type OnlineSourceSearchParams =
  paths['/api/knowledge/crawl/config']['get']['parameters']['query'];
export type OnlineSourceSearchResponse =
  paths['/api/knowledge/crawl/config']['get']['responses']['200']['content']['application/json'];

export type OnlineSourceCreateRequestBody = Omit<
  paths['/api/knowledge/crawl/config']['post']['requestBody']['content']['application/json'],
  'include_patterns' | 'exclude_patterns'
> & {
  include_patterns?: string[];
  exclude_patterns?: string[];
};

export type OnlineSourceUpdateRequestBody = Omit<
  components['schemas']['PatchedKnowledgeBaseCrawlConfigRequest'],
  'include_patterns' | 'exclude_patterns'
> & {
  include_patterns?: string[];
  exclude_patterns?: string[];
};

export type StartOnlineSourceResponse =
  paths['/api/knowledge/crawl/config/{id}/start']['post']['responses']['201']['content']['application/json'];

export type CancelOnlineSourceResponse =
  paths['/api/knowledge/crawl/config/{id}/executions/{execution_id}/cancel']['post']['responses']['200']['content']['application/json'];

export type OnlineSourceCreateResponse =
  paths['/api/knowledge/crawl/config']['post']['responses']['201']['content']['application/json'];

export type OnlineSourceDeleteResponse =
  paths['/api/knowledge/crawl/config/{id}']['delete']['responses']['204']['content'];

export type OnlineSourcePagesSearchResponse =
  paths['/api/knowledge/crawl/pages/search']['get']['responses']['200']['content']['application/json'];

export type OnlineSourcePagesSearchParams =
  paths['/api/knowledge/crawl/pages/search']['get']['parameters']['query'];

export type OnlineSourcePageDetails =
  paths['/api/knowledge/crawl/pages/{id}']['get']['responses']['200']['content']['application/json'];

export type OnlineSourceSlim =
  components['schemas']['CrawlConfigNamesResponse'];

export const getKnowledgeSourceSearch = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<KnowledgeSourceSearchParams>
): Promise<KnowledgeSourceSearchResponse> => {
  const res = await instance.get<KnowledgeSourceSearchResponse>(
    `knowledge/source/search`,
    options
  );

  return res.data;
};

export const getKnowledgeSourceById = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<{ catalog_id?: string }>
): Promise<KnowledgeSource> => {
  const res = await instance.get<KnowledgeSource>(
    `knowledge/source/${id}`,
    options
  );
  return res.data;
};

export const createKnowledgeSource = async (
  instance: AxiosInstance,
  payload: KnowledgeSourceCreateRequestBody,
  options?: RequestOptions
): Promise<KnowledgeSource> => {
  const res = await instance.post<KnowledgeSource>(
    `knowledge/source/add`,
    payload,
    options
  );
  return res.data;
};

export const deleteKnowledgeSource = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<KnowledgeSourceDeleteResponse> => {
  const url = `knowledge/source/${id}`;
  const res = await instance.delete<KnowledgeSourceDeleteResponse>(
    url,
    options
  );
  return res.data;
};

export type KnowledgeSourceDownloadParams =
  paths['/api/knowledge/source/{id}/download']['get']['parameters']['query'];

export const downloadKnowledgeSource = async (
  instance: AxiosInstance,
  id: string,
  options: RequestOptionsWithParams<KnowledgeSourceDownloadParams>
): Promise<Blob> => {
  const url = `knowledge/source/${id}/download`;
  const res = await instance.get<Blob>(url, {
    ...options,
    responseType: 'blob',
  });
  return res.data;
};

export const getOnlineSourceSearch = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<OnlineSourceSearchParams>
): Promise<OnlineSourceSearchResponse> => {
  const res = await instance.get<OnlineSourceSearchResponse>(
    `knowledge/crawl/config`,
    options
  );

  return res.data;
};

export const getOnlineSourceNames = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<OnlineSourceSlim[]> => {
  const res = await instance.get<OnlineSourceSlim[]>(
    `knowledge/crawl/config/names`,
    options
  );

  return res.data;
};

export const createOnlineSource = async (
  instance: AxiosInstance,
  payload: OnlineSourceCreateRequestBody,
  options?: RequestOptions
): Promise<OnlineSourceCreateResponse> => {
  const res = await instance.post<OnlineSourceCreateResponse>(
    `knowledge/crawl/config`,
    payload,
    options
  );
  return res.data;
};

export const updateOnlineSource = async (
  instance: AxiosInstance,
  id: string,
  payload: OnlineSourceUpdateRequestBody,
  options?: RequestOptions
): Promise<OnlineSource> => {
  const url = `knowledge/crawl/config/${id}`;
  const res = await instance.patch<OnlineSource>(url, payload, options);
  return res.data;
};

export const setOnlineSourceActiveStatus = async (
  instance: AxiosInstance,
  id: string,
  isActive: boolean,
  options?: RequestOptions
): Promise<void> => {
  const url = `knowledge/crawl/config/${id}/activestatus`;
  await instance.patch(url, { is_active: isActive }, options);
};

export const startOnlineSourceExecution = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<StartOnlineSourceResponse> => {
  const url = `/knowledge/crawl/config/${id}/start`;
  const res = await instance.post<StartOnlineSourceResponse>(url, {}, options);
  return res.data;
};

export const cancelOnlineSourceExecution = async (
  instance: AxiosInstance,
  id: string,
  executionId: string,
  options?: RequestOptions
): Promise<CancelOnlineSourceResponse> => {
  const url = `knowledge/crawl/config/${id}/executions/${executionId}/cancel`;
  const res = await instance.post<CancelOnlineSourceResponse>(url, {}, options);
  return res.data;
};

export const deleteOnlineSource = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<OnlineSourceDeleteResponse> => {
  const url = `knowledge/crawl/config/${id}`;
  const res = await instance.delete<OnlineSourceDeleteResponse>(url, options);
  return res.data;
};

export const getOnlineSourcePagesSearch = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<OnlineSourcePagesSearchParams>
): Promise<OnlineSourcePagesSearchResponse> => {
  const res = await instance.get<OnlineSourcePagesSearchResponse>(
    `knowledge/crawl/pages/search`,
    options
  );
  return res.data;
};

export const setOnlineSourcePageActiveStatus = async (
  instance: AxiosInstance,
  id: string,
  isActive: boolean,
  options?: RequestOptions
): Promise<void> => {
  const url = `knowledge/source/${id}/activestatus`;
  await instance.patch(url, { is_active_by_user: isActive }, options);
};

export const getOnlineSourcePageDetails = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<{ catalog_id?: string }>
): Promise<OnlineSourcePageDetails> => {
  const res = await instance.get<OnlineSourcePageDetails>(
    `knowledge/crawl/pages/${id}`,
    options
  );

  return res.data;
};

export type KnowledgeCatalog = Mutable<
  paths['/api/knowledge/catalogs/{id}']['get']['responses']['200']['content']['application/json']
> & { plugin_app?: unknown[] };
export type KnowledgeCatalogListResponse = PaginatedResponse<KnowledgeCatalog>;
export type KnowledgeCatalogListParams = Required<
  paths['/api/knowledge/catalogs']['get']['parameters']
>['query'];
type KnowledgeCatalogCreatePayload =
  paths['/api/knowledge/catalogs']['post']['requestBody']['content']['application/json'];
type KnowledgeCatalogUpdatePayload = Required<
  paths['/api/knowledge/catalogs/{id}']['patch']
>['requestBody']['content']['application/json'];

export const getKnowledgeCatalogs = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<KnowledgeCatalogListParams>
): Promise<KnowledgeCatalogListResponse> => {
  const res = await instance.get<KnowledgeCatalogListResponse>(
    `knowledge/catalogs`,
    options
  );
  return res.data;
};

export const createKnowledgeCatalog = async (
  instance: AxiosInstance,
  payload: KnowledgeCatalogCreatePayload,
  options?: RequestOptions
): Promise<KnowledgeCatalog> => {
  const res = await instance.post<KnowledgeCatalog>(
    `knowledge/catalogs`,
    payload,
    options
  );
  return res.data;
};

export const getKnowledgeCatalogById = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<KnowledgeCatalog> => {
  const res = await instance.get<KnowledgeCatalog>(
    `knowledge/catalogs/${id}`,
    options
  );
  return res.data;
};

export const updateKnowledgeCatalog = async (
  instance: AxiosInstance,
  id: string,
  payload: KnowledgeCatalogUpdatePayload,
  options?: RequestOptions
): Promise<KnowledgeCatalog> => {
  const res = await instance.patch<KnowledgeCatalog>(
    `knowledge/catalogs/${id}`,
    payload,
    options
  );
  return res.data;
};

export const deleteKnowledgeCatalog = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(`knowledge/catalogs/${id}`, options);
};

export type KnowledgeCatalogAskRequest =
  paths['/api/knowledge/catalogs/{id}/ask']['post']['requestBody']['content']['application/json'];

export type KnowledgeCatalogAskResponse =
  paths['/api/knowledge/catalogs/{id}/ask']['post']['responses']['200']['content']['application/json'];

export type KnowledgeCatalogAskCitation =
  KnowledgeCatalogAskResponse['citations'][number];

export const askKnowledgeCatalog = async (
  instance: AxiosInstance,
  catalogId: string,
  payload: KnowledgeCatalogAskRequest,
  options?: RequestOptions
): Promise<KnowledgeCatalogAskResponse> => {
  const res = await instance.post<KnowledgeCatalogAskResponse>(
    `knowledge/catalogs/${catalogId}/ask`,
    payload,
    options
  );
  return res.data;
};

export type KnowledgeCatalogSharingSettings = NonNullable<
  components['schemas']['PatchedKnowledgeBaseCatalogRequest']['sharing_settings']
>;

export type KnowledgePageKey = Extract<
  EmployeePageConfigType,
  `knowledge_${string}`
>;

type KnowledgePageConfig = EmployeePageConfig<KnowledgePageKey>;

export const putKnowledgePageConfig = async (
  instance: AxiosInstance,
  pageKey: KnowledgePageKey,
  payload: KnowledgePageConfig,
  options?: RequestOptions
): Promise<KnowledgePageConfig> => {
  const res = await instance.put<KnowledgePageConfig>(
    `employee/mine/configs/page/${pageKey}`,
    payload,
    options
  );

  return res.data;
};

export class KnowledgeService {
  public getKnowledgeSourceSearch;
  public getKnowledgeSourceById;
  public createKnowledgeSource;
  public deleteKnowledgeSource;
  public downloadKnowledgeSource;
  public getOnlineSourceSearch;
  public getOnlineSourceNames;
  public createOnlineSource;
  public updateOnlineSource;
  public setOnlineSourceActiveStatus;
  public startOnlineSourceExecution;
  public cancelOnlineSourceExecution;
  public deleteOnlineSource;
  public getOnlineSourcePagesSearch;
  public setOnlineSourcePageActiveStatus;
  public getOnlineSourcePageDetails;
  public putKnowledgePageConfig;
  public getKnowledgeCatalogs;
  public createKnowledgeCatalog;
  public getKnowledgeCatalogById;
  public updateKnowledgeCatalog;
  public deleteKnowledgeCatalog;
  public askKnowledgeCatalog;

  constructor(private instance: AxiosInstance) {
    this.getKnowledgeSourceSearch = curry1(getKnowledgeSourceSearch)(
      this.instance
    );
    this.getKnowledgeSourceById = curry1(getKnowledgeSourceById)(this.instance);
    this.createKnowledgeSource = curry1(createKnowledgeSource)(this.instance);
    this.deleteKnowledgeSource = curry1(deleteKnowledgeSource)(this.instance);
    this.downloadKnowledgeSource = curry1(downloadKnowledgeSource)(this.instance);
    this.getOnlineSourceSearch = curry1(getOnlineSourceSearch)(this.instance);
    this.getOnlineSourceNames = curry1(getOnlineSourceNames)(this.instance);
    this.createOnlineSource = curry1(createOnlineSource)(this.instance);
    this.updateOnlineSource = curry1(updateOnlineSource)(this.instance);
    this.setOnlineSourceActiveStatus = curry1(setOnlineSourceActiveStatus)(
      this.instance
    );
    this.startOnlineSourceExecution = curry1(startOnlineSourceExecution)(
      this.instance
    );
    this.cancelOnlineSourceExecution = curry1(cancelOnlineSourceExecution)(
      this.instance
    );
    this.deleteOnlineSource = curry1(deleteOnlineSource)(this.instance);
    this.getOnlineSourcePagesSearch = curry1(getOnlineSourcePagesSearch)(
      this.instance
    );
    this.setOnlineSourcePageActiveStatus = curry1(
      setOnlineSourcePageActiveStatus
    )(this.instance);
    this.getOnlineSourcePageDetails = curry1(getOnlineSourcePageDetails)(
      this.instance
    );
    this.putKnowledgePageConfig = curry1(putKnowledgePageConfig)(this.instance);
    this.getKnowledgeCatalogs = curry1(getKnowledgeCatalogs)(this.instance);
    this.createKnowledgeCatalog = curry1(createKnowledgeCatalog)(this.instance);
    this.getKnowledgeCatalogById = curry1(getKnowledgeCatalogById)(
      this.instance
    );
    this.updateKnowledgeCatalog = curry1(updateKnowledgeCatalog)(this.instance);
    this.deleteKnowledgeCatalog = curry1(deleteKnowledgeCatalog)(this.instance);
    this.askKnowledgeCatalog = curry1(askKnowledgeCatalog)(this.instance);
  }
}
